// WINDOW-clause definition parser. Parses `WINDOW w AS (…) [, w2 AS (…)]` into
// `NamedWindowDef[]`, reusing the shared OVER-body parser (`parseWindowSpecBody`)
// so each definition body is byte-identical to an inline `OVER (…)` spec.
//
// Named windows exist on all five engines (SQL Server 2022 / Oracle 21c gained
// the clause). A bare `window` identifier never populates `QueryStructure.windows`
// because a definition is only recorded when a `<ident> [AS] (` shape parses
// (see `parseDefinition` below — the `(` is required).
//
// Best-effort / never-throw: a malformed definition (`WINDOW w` with no body,
// or a leading token that isn't an identifier) is skipped, not fatal — the
// user is mid-type and the clause must degrade gracefully.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Query Structure Extractor —
// SELECT (WINDOW clause definitions + OVER named-window linking).

import { rangeFromOffsets } from '../../sql-grammar/document/lineIndex';
import { readIdentifier } from '../scope/identifiers';
import type { NamedWindowDef, WindowSpec } from '../types';

import type { SelectExtractCtx } from './extractQueryStructure.select';
import { parseWindowSpecBody } from './extractQueryStructure.window';
import { findTopLevelCommas, trimRange } from './scanner';

// Parse the content of a WINDOW clause — the byte range AFTER the `WINDOW`
// keyword up to the next clause boundary (ORDER BY / LIMIT / set-op / branch
// end). Splits on top-level commas and parses each `name AS (…)` definition.
export function extractWindowClause(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): NamedWindowDef[] {
  const { source, options } = ctx;
  const trimmed = trimRange(source, start, end, options);
  if (trimmed.startOffset >= trimmed.endOffset) return [];

  const commas = findTopLevelCommas(source, trimmed.startOffset, trimmed.endOffset, options);
  const out: NamedWindowDef[] = [];
  let segStart = trimmed.startOffset;
  const pushSeg = (a: number, b: number): void => {
    const def = parseDefinition(ctx, a, b);
    if (def) out.push(def);
  };
  for (const c of commas) {
    pushSeg(segStart, c);
    segStart = c + 1;
  }
  pushSeg(segStart, trimmed.endOffset);
  return out;
}

// Parse a single `name AS ( … )` definition. Returns undefined when the segment
// has no leading identifier or no parenthesised body.
function parseDefinition(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): NamedWindowDef | undefined {
  const { source, options, lineIdx } = ctx;
  const r = trimRange(source, start, end, options);
  if (r.startOffset >= r.endOffset) return undefined;

  const nameTok = readIdentifier(source, r.startOffset, r.endOffset, options);
  if (!nameTok) return undefined;

  // Skip whitespace, then an optional `AS` keyword before the body paren.
  let i = nameTok.end;
  while (i < r.endOffset && /\s/.test(source[i])) i++;
  if (
    source.slice(i, i + 2).toUpperCase() === 'AS'
    && /[^A-Za-z0-9_]/.test(source[i + 2] ?? ' ')
  ) {
    i += 2;
    while (i < r.endOffset && /\s/.test(source[i])) i++;
  }

  if (source[i] !== '(') return undefined; // malformed `WINDOW w` — no body.

  const body = parseWindowSpecBody(ctx, i, r.endOffset);
  const defEnd = body.closeIdx > 0 ? body.closeIdx : r.endOffset;
  const spec: WindowSpec = {
    range: rangeFromOffsets(lineIdx, i, defEnd),
    ...(body.partitionBy && body.partitionBy.length ? { partitionBy: body.partitionBy } : {}),
    ...(body.orderBy && body.orderBy.length ? { orderBy: body.orderBy } : {}),
    ...(body.frame ? { frame: body.frame } : {}),
  };
  return {
    name: nameTok.value,
    spec,
    range: rangeFromOffsets(lineIdx, r.startOffset, defEnd),
  };
}
